import React, { useEffect, useRef, useState } from 'react';
import { Button, Drawer, Menu, Typography } from 'antd';
import {
  MenuOutlined,
  HomeOutlined,
  CalendarOutlined,
  ReloadOutlined,
  SearchOutlined,
  RetweetOutlined,
  CheckCircleOutlined,
  UserOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ZoomDropdown from '@crm/components/Shared/ZoomDropdown';
import NotificationBell from '@crm/components/Shared/NotificationBell';
import { useCognitoAuth } from '@crm/contexts/CognitoAuthContext';
import apiService from '@crm/services/api';
import { useCccNotificationFeed } from '../hooks/useCccNotifications';
import DEPLOYMENT_CONFIG from '@crm/config/deployment';
import CccTenantSwitcher from '../components/CccTenantSwitcher';
import { isPlatformAdmin } from '../utils/isPlatformAdmin';
import { canManageCccUsers } from '../utils/cccUserManagement';
import { clearAuthSession } from '../auth/sessionBridge';
import { CCC_DRAWER_MENU_STYLES } from './CccDrawerMenuStyles';
import '@crm/components/Comercial/ComercialMainLayout.css';
import '../styles/layout.css';

const { Text } = Typography;

const CCCMainLayout: React.FC = () => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<{
    name?: string;
    username?: string;
    email?: string;
    avatar_url?: string;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useCognitoAuth();
  const { notifications, loading, fetchNotifications, markAsRead, markAllAsRead, clearNotifications } =
    useCccNotificationFeed();

  useEffect(() => {
    if (authUser && (authUser.username || authUser.email)) {
      setCurrentUser(authUser as typeof currentUser);
    }
  }, [authUser]);

  useEffect(() => {
    (async () => {
      try {
        const me = await apiService.getCurrentUser();
        if (me) setCurrentUser(me);
      } catch {
        /* CccSessionBootstrap also hydrates; ignore transient proxy errors */
      }
    })();
  }, []);

  const canManageUsers = canManageCccUsers(
    (authUser as Record<string, unknown>) || (currentUser as Record<string, unknown>)
  );

  const menuItems = [
    { key: '/', icon: <HomeOutlined />, label: 'Página Principal' },
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/calendario', icon: <CalendarOutlined />, label: 'Calendário' },
    { key: '/recuperacao', icon: <ReloadOutlined />, label: 'Recuperação' },
    { key: '/prospeccao', icon: <SearchOutlined />, label: 'Prospeção' },
    { key: '/retencao', icon: <RetweetOutlined />, label: 'Reposição' },
    { key: '/resolucao', icon: <CheckCircleOutlined />, label: 'Resolução' },
    ...(canManageUsers
      ? [{ key: '/utilizadores', icon: <UserOutlined />, label: 'Gestão de Utilizadores' }]
      : []),
  ];

  const selectedKey =
    menuItems.find((m) => location.pathname === m.key)?.key ||
    menuItems
      .filter((m) => m.key !== '/' && location.pathname.startsWith(m.key))
      .sort((a, b) => b.key.length - a.key.length)[0]?.key ||
    '/';

  const admin = isPlatformAdmin(
    (authUser as Record<string, unknown>) || (currentUser as Record<string, unknown>)
  );

  const u = currentUser || (authUser as Record<string, unknown>) || {};
  const name =
    String(u.username || u.name || u.email || '').trim() || 'Utilizador';
  const avatarSrc = DEPLOYMENT_CONFIG.getResourceUrl(
    u.avatar_url as string | undefined
  );
  const initials = (name || 'U').slice(0, 1).toUpperCase();

  return (
    <div ref={mainContainerRef} className="ccc-layout comercial-layout">
      <nav className="nav-bar">
        <div className="nav-left">
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            className="crm-hamburger-btn"
            aria-label="Menu"
            style={{
              color: 'white',
              fontSize: 20,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12,
            }}
          />
          <div className="nav-logo">
            <img src="/LOGO-Wondertrade.png" alt="Wondertrade Logo" />
          </div>
          <CccTenantSwitcher isAdmin={admin} />
        </div>
        <div className="nav-right">
          <ZoomDropdown
            containerRef={mainContainerRef}
            storageKey="ccc_zoom_option"
            defaultOption="0"
            variant="dark"
          />
          <NotificationBell
            variant="dark"
            notifications={notifications}
            loading={loading}
            onMarkAsRead={(id) => markAsRead(Number(id))}
            onMarkAllAsRead={() => markAllAsRead()}
            onClear={(scope) => clearNotifications(scope)}
            onRefresh={fetchNotifications}
            onNotificationClick={(n) => {
              const url = n?.action_url;
              if (typeof url === 'string' && url.startsWith('/')) {
                navigate(url);
              }
            }}
          />
          <div className="nav-user" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt="Avatar"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                className="user-avatar"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#eee',
                  color: '#555',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                {initials}
              </div>
            )}
            <span>{name}</span>
          </div>
          <button
            type="button"
            className="logout-btn"
            onClick={() => {
              clearAuthSession();
              window.location.href = '/login';
            }}
          >
            Sair
          </button>
        </div>
      </nav>

      <Drawer
        title={
          <Text strong style={{ fontSize: 20, letterSpacing: 0.5, color: '#ef4444', fontWeight: 600 }}>
            Menu
          </Text>
        }
        placement="left"
        closable
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
        style={{ borderTopRightRadius: 20, borderBottomRightRadius: 20 }}
        headerStyle={{
          borderBottom: '1px solid rgba(239, 68, 68, 0.1)',
          background: 'linear-gradient(180deg, #0f0f0f 0%, #1a1a1a 100%)',
          borderTopRightRadius: 20,
          padding: '20px 24px',
        }}
        bodyStyle={{ padding: '12px 8px', background: '#0f0f0f' }}
        className="crm-drawer-menu"
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            setDrawerOpen(false);
          }}
          style={{
            borderRight: 0,
            height: '100%',
            fontSize: 15,
            padding: '8px 0',
            background: 'transparent',
          }}
          theme="dark"
          className="crm-menu-list"
        />
        <style>{CCC_DRAWER_MENU_STYLES}</style>
      </Drawer>

      <main className="main-content gestao-marcacoes">
        <Outlet />
      </main>
    </div>
  );
};

export default CCCMainLayout;
